import { MemberPortalApi, memberErrorMessage, type PortalTrip } from "@/api/perkpoint/memberPortal";
import { refreshWallet } from "./wallet.service";

export type TripKind = "stay" | "flight";
export type TripStatus = "Upcoming" | "Completed" | "Cancelled";

export type MemberTrip = {
  id: string;
  reference: string;
  kind: TripKind;
  status: TripStatus;
  title: string;
  subtitle: string;
  image: string;
  startDate: string;
  endDate: string;
  detail: string;
  travellers: number;
  currency: string;
  totalInr: number;
  cashPaid: number;
  pointsUsed: number;
  pointsEarned: number;
  pointsPending: boolean;
  pointsAvailableAt: string | null;
  savedInr: number;
};

function fromApi(trip: PortalTrip): MemberTrip {
  return {
    id: trip.id,
    reference: trip.reference,
    kind: trip.kind,
    status: trip.status,
    title: trip.title,
    subtitle: trip.subtitle,
    image: trip.image,
    startDate: trip.start_date,
    endDate: trip.end_date,
    detail: trip.detail,
    travellers: trip.travellers,
    currency: trip.currency,
    totalInr: trip.total,
    cashPaid: trip.cash_paid,
    pointsUsed: trip.points_used,
    pointsEarned: trip.points_earned,
    pointsPending: Boolean(trip.points_pending),
    pointsAvailableAt: trip.points_available_at ?? null,
    savedInr: trip.saved,
  };
}

export async function getTrips(): Promise<MemberTrip[]> {
  const rows = await MemberPortalApi.trips();
  return rows.map(fromApi);
}

export async function saveTrip(draft: {
  kind: TripKind;
  title: string;
  subtitle: string;
  image: string;
  startDate: string;
  endDate: string;
  detail: string;
  travellers: number;
  totalInr: number;
  pointsUsed: number;
  guest: { name: string; email: string; mobile: string };
}): Promise<{
  trip: MemberTrip;
  tierBefore: string;
  tierAfter: string;
  earnState: "pending" | "available" | "none";
  earnAvailableAt: string | null;
  availablePoints: number;
  pendingPoints: number;
}> {
  try {
    const result = await MemberPortalApi.createTrip({
      kind: draft.kind,
      title: draft.title,
      subtitle: draft.subtitle,
      image: draft.image,
      start_date: draft.startDate,
      end_date: draft.endDate,
      detail: draft.detail,
      travellers: draft.travellers,
      total: draft.totalInr,
      points_to_use: draft.pointsUsed,
      guest: draft.guest,
    });
    await refreshWallet();
    window.dispatchEvent(new Event("member-trips"));
    return {
      trip: fromApi(result.trip),
      tierBefore: result.tier_before || "Member",
      tierAfter: result.tier_after || result.tier_before || "Member",
      earnState: result.earn?.state || "none",
      earnAvailableAt: result.earn?.available_at ?? null,
      availablePoints: result.points?.available ?? 0,
      pendingPoints: result.points?.pending ?? 0,
    };
  } catch (error) {
    throw new Error(memberErrorMessage(error, "Could not confirm this booking."));
  }
}

export async function cancelTrip(id: string): Promise<MemberTrip | null> {
  try {
    const result = await MemberPortalApi.cancelTrip(id);
    await refreshWallet();
    window.dispatchEvent(new Event("member-trips"));
    return fromApi(result.trip);
  } catch (error) {
    throw new Error(memberErrorMessage(error, "Could not cancel this trip."));
  }
}
